/** DEAL ENGINE (v1 js/deal-engine.js, July-2026 distress-keyword research) —
 * the score + suggested-offer card from v1's Listing panel, transcribed as
 * pure functions over v2's Lead + edits.
 *
 * v2 scope notes:
 *  - OUTCOME_LOG rides in via rec_org_settings (v1's sweep keeps writing it);
 *    v2 READS it for back-on-market: +25 score and the bom offer band.
 *  - rehab: v1 reads p.rehab / NR_BY; v2 uses the lead's rehab edit when
 *    present, else 0 (MAO then reads conservative-high, same as v1 with no
 *    rehab known).
 */
import type { Lead } from "../../types";

// ---- OUTCOME_LOG (v1 deOutcomeSweep entries, read-only in v2) ---------------

export interface OutcomeEntry {
  listDate: string | null;
  pendingAt: string | null;
  bomAt: string | null;
  soldAt: string | null;
  listPrice: number | null;
  soldPrice: number | null;
  cuts: { from: number | null; to: number | null }[];
}

const onum = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

export function narrowOutcome(raw: unknown): OutcomeEntry | null {
  if (raw === null || raw === undefined || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
  const cutsRaw = Array.isArray(r["cuts"]) ? r["cuts"] : [];
  const cuts = cutsRaw
    .filter((c): c is Record<string, unknown> => c !== null && typeof c === "object" && !Array.isArray(c))
    .map((c) => ({ from: onum(c["from"]), to: onum(c["to"]) }));
  return {
    listDate: str(r["listDate"]), pendingAt: str(r["pendingAt"]), bomAt: str(r["bomAt"]), soldAt: str(r["soldAt"]),
    listPrice: onum(r["listPrice"]), soldPrice: onum(r["soldPrice"]), cuts,
  };
}

/** v1 deKey(): OUTCOME_LOG is keyed by the raw address, lowercased+trimmed. */
export function outcomeFor(outcomeLog: unknown, address: string): OutcomeEntry | null {
  if (outcomeLog === null || typeof outcomeLog !== "object" || Array.isArray(outcomeLog)) return null;
  return narrowOutcome((outcomeLog as Record<string, unknown>)[String(address).toLowerCase().trim()]);
}

/** v1's bom test: back on market = a bomAt not superseded by a later pending. */
export const deBom = (ol: OutcomeEntry | null): boolean =>
  !!(ol && ol.bomAt && (!ol.pendingAt || (Date.parse(ol.bomAt) || 0) >= (Date.parse(ol.pendingAt) || 0)));

export interface DeTier {
  id: "investor" | "condition" | "priced" | "estate";
  name: string;
  kws: string[];
}

/** research §1 — verbatim keyword tiers, order = priority. */
export const DE_TIERS: DeTier[] = [
  { id: "investor", name: "Investor-ready", kws: ["investor special", "investor-special", "as-is", "as is", "cash only", "cash-only", "handyman special", "handyman", "sweat equity", "rough condition", "sold as-is", "sold as is"] },
  { id: "condition", name: "Condition", kws: ["tlc", "fixer", "fixer-upper", "fixer upper", "needs work", "needs repair", "needs repairs", "needs updating", "good bones", "bring your toolbox", "needs love", "some work"] },
  { id: "priced", name: "Priced & motivated", kws: ["priced to sell", "motivated seller", "motivated", "must sell", "must go", "bring offers", "bring all offers", "make an offer", "make offer", "price reduced", "priced below", "wont last", "act fast"] },
  { id: "estate", name: "Estate & probate", kws: ["estate sale", "estate", "probate", "inherited", "executor", "trustee sale"] },
];

/** research §3 — soft counties (refresh quarterly). */
export const DE_SOFT_COUNTIES = ["indian river", "hernando", "lake", "polk", "volusia", "seminole", "osceola", "manatee", "marion"];

const money = (v: unknown): number => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function deKeywords(desc: string | null, notes: string | null): { kws: string[]; tier: DeTier | null } {
  const t = ((desc ?? "") + " " + (notes ?? "")).toLowerCase();
  const hits: string[] = [];
  let tier: DeTier | null = null;
  for (const T of DE_TIERS) {
    for (const k of T.kws) {
      if (t.includes(k)) {
        hits.push(k);
        if (!tier) tier = T;
      }
    }
  }
  return { kws: [...new Set(hits)], tier };
}

export function deCountySoft(county: string | null): boolean {
  const c = String(county ?? "").toLowerCase().replace(/ county/, "").trim();
  return DE_SOFT_COUNTIES.some((s) => c.includes(s));
}

/** best-known days on market: CDOM, else the outcome log's listDate, else
 * days since the lead arrived (v1 deDom order: cdom → ol.listDate → _ts). */
export function deDom(lead: Lead, now: number, ol: OutcomeEntry | null = null): number {
  if (lead.cdom !== null && lead.cdom > 0) return lead.cdom;
  if (ol?.listDate) {
    const t = Date.parse(ol.listDate);
    if (Number.isFinite(t)) {
      const d = Math.floor((now - t) / 86400000);
      if (d >= 0) return d;
    }
  }
  if (lead.createdAt) {
    const d = Math.floor((now - lead.createdAt) / 86400000);
    if (d >= 0) return d;
  }
  return 0;
}

export const deCuts = (lead: Lead): number => (lead.priceDrops.length ? lead.priceDrops.length : lead.priceDrop ? 1 : 0);

export function deLastCutAt(lead: Lead): number {
  let t = 0;
  for (const d of lead.priceDrops) {
    const x = d.date ? Date.parse(d.date) || 0 : 0;
    if (x > t) t = x;
  }
  return t;
}

/** score 0–100: keyword tier × DOM × cuts × county × BOM (v1 verbatim).
 * Off-market scores 0. */
export function deScore(lead: Lead, notes: string | null, now: number, ol: OutcomeEntry | null = null): number {
  if (lead.marketStatus === "off") return 0;
  const kw = deKeywords(lead.desc, notes);
  let s = 0;
  s += kw.tier ? ({ investor: 30, condition: 22, estate: 18, priced: 15 }[kw.tier.id] ?? 0) : 0;
  const dom = deDom(lead, now, ol);
  s += dom >= 90 ? 30 : dom >= 60 ? 24 : dom >= 30 ? 18 : dom >= 14 ? 10 : 5;
  const cuts = deCuts(lead);
  s += Math.min(cuts * 8, 16);
  if (cuts && now - deLastCutAt(lead) < 7 * 86400000) s += 6; // cut this week = live signal
  if (deCountySoft(lead.county)) s += 15;
  if (deBom(ol)) s += 25; // back on market = hottest
  return Math.min(100, s);
}

/** research §6 matrix — % of CURRENT list price; bom band first (v1 order). */
export function deOfferPct(lead: Lead, notes: string | null, now: number, ol: OutcomeEntry | null = null): { lo: number; hi: number; bom: boolean } {
  const kw = deKeywords(lead.desc, notes);
  const dom = deDom(lead, now, ol);
  const soft = deCountySoft(lead.county);
  const cuts = deCuts(lead);
  const bom = deBom(ol);
  let lo: number, hi: number;
  if (bom) { lo = soft ? 0.78 : 0.83; hi = soft ? 0.85 : 0.88; }
  else if (dom >= 90 || cuts >= 3) { lo = soft ? 0.70 : 0.75; hi = soft ? 0.78 : 0.82; }
  else if (dom >= 60 || cuts >= 2) { lo = soft ? 0.78 : 0.82; hi = soft ? 0.84 : 0.88; }
  else if (dom >= 30) { lo = soft ? 0.82 : 0.86; hi = soft ? 0.88 : 0.91; }
  else if (kw.tier?.id === "investor") { lo = soft ? 0.86 : 0.90; hi = soft ? 0.90 : 0.94; }
  else if (kw.tier?.id === "estate") { lo = soft ? 0.82 : 0.86; hi = soft ? 0.88 : 0.90; }
  else if (kw.tier?.id === "priced") { lo = soft ? 0.90 : 0.93; hi = soft ? 0.94 : 0.97; }
  else { lo = soft ? 0.88 : 0.92; hi = soft ? 0.92 : 0.95; } // fresh condition/no-keyword
  return { lo, hi, bom };
}

export interface DeOffer {
  lo: number;
  hi: number;
  mao: number | null; // 0.72×ARV − rehab (null without an ARV)
  capped: boolean;
  pctLo: number;
  pctHi: number;
  bom: boolean;
  list: number;
}

export function deSuggestOffer(
  lead: Lead,
  edits: Record<string, unknown> | undefined,
  notes: string | null,
  now: number,
  ol: OutcomeEntry | null = null,
): DeOffer | null {
  const list = money(lead.price);
  if (!list) return null;
  const pct = deOfferPct(lead, notes, now, ol);
  const arv = money(edits?.["compedArv"] ?? edits?.["arv"] ?? edits?.["listedArv"] ?? lead.arv);
  const rehab = money(edits?.["rehab"] ?? edits?.["rehabEst"]);
  const mao = arv ? Math.max(0, Math.round(arv * 0.72 - rehab)) : null;
  let lo = Math.round(list * pct.lo);
  let hi = Math.round(list * pct.hi);
  let capped = false;
  if (mao !== null && mao < hi) {
    hi = Math.min(hi, mao);
    lo = Math.min(lo, hi);
    capped = true;
  }
  return { lo, hi, mao, capped, pctLo: pct.lo, pctHi: pct.hi, bom: pct.bom, list };
}

/** v1's badge hue thresholds. */
export const deBadgeHue = (s: number): string => (s >= 70 ? "red" : s >= 55 ? "amber" : s >= 40 ? "blue" : "slate");
